import React, { useState} from 'react';
import {Button, Dropdown, Form, FormInstance, Input, Radio, Select, Switch} from 'antd';
import {NginxLocationConf, ProxyPassType, ProxyPassTypeExt} from '../entities/NginxLocationConf';
import {
    NginxLocationConfiguration,
    NginxLocationConfigurationType,
    NginxLocationConfigurationTypeExt,
} from '../entities/NginxLocationConfiguration';
import NginxService from '../http/NginxService';
import './LocationForm.css';
import {ServiceInfo} from "../entities/ServiceInfo";
import {toast} from "react-toastify";
import TextArea from "antd/es/input/TextArea";
import ClientMaxBodySizeInput from "./ClientMaxBodySizeInput";
import ProxyReadTimeoutInput from "./ProxyReadTimeoutInput";

const { Option } = Select;
const INDENT = '    ';

interface LocationFormProps {
    form: FormInstance<NginxLocationConf>;
    conf: NginxLocationConf | null;
    onSave: (location: NginxLocationConf) => void;
}

const LocationForm: React.FC<LocationFormProps> = ({ form, conf, onSave }) => {
    const [appKeys, setAppKeys] = useState<ServiceInfo[]>([]);
    const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
    const proxyPassTypeValue = Form.useWatch<string>("proxyPassType", form);
    const configurations = Form.useWatch<NginxLocationConfiguration[]>("configurations", form);

    const handleFinish = (values: NginxLocationConf) => {
        if (!values.path || values.path.trim() === '') {
            toast.error("请填写路径名称");
            return;
        }
        if (values.proxyPassType !== ProxyPassType[ProxyPassType.NONE] && (!values.proxyPass || values.proxyPass.trim() === '')) {
            toast.error("请填写代理地址");
            return;
        }
        if (values.proxyPassType === ProxyPassType[ProxyPassType.FRONTEND] &&
            (!values.proxyPass || !values.proxyHeaderHost)) {
            toast.error("请填写完整的前端代理信息");
            return;
        }
        // 修整字符串
        const updated: NginxLocationConf = {
            ...conf,
            ...values,
            path: values.path.trim(),
            proxyPass: values.proxyPass?.trim(),
            proxyHeaderHost: values.proxyHeaderHost?.trim(),
            configurations: values.configurations?.map(config => {
                return new NginxLocationConfiguration(config.type, config.value);
            }) || []
        };
        onSave(updated);
    };

    const addedConfigTypes = configurations?.map(config =>
        NginxLocationConfigurationTypeExt.fromString(config.type)
    ) || [];

    const availableConfigTypes = NginxLocationConfigurationTypeExt.values().filter(
        key => !addedConfigTypes.includes(key)
    );

    const removeLeadingSpaces = (line: string): number => {
        let removed = 0;
        while (removed < INDENT.length && removed < line.length && line[removed] === ' ') {
            removed += 1;
        }
        return removed;
    };

    const handleCustomConfigTabKeyDown = (
        e: React.KeyboardEvent<HTMLTextAreaElement>,
        name: number,
    ) => {
        if (e.key !== 'Tab') {
            return;
        }
        e.preventDefault();

        const textarea = e.currentTarget;
        const fieldPath: ['configurations', number, 'value'] = ['configurations', name, 'value'];
        const value: string = form.getFieldValue(fieldPath) || '';
        const start = textarea.selectionStart ?? 0;
        const end = textarea.selectionEnd ?? 0;

        let nextValue = value;
        let nextSelectionStart = start;
        let nextSelectionEnd = end;

        if (start === end) {
            const lineStart = value.lastIndexOf('\n', start - 1) + 1;
            if (e.shiftKey) {
                const removeCount = removeLeadingSpaces(value.slice(lineStart));
                if (removeCount === 0) {
                    return;
                }
                nextValue = value.slice(0, lineStart) + value.slice(lineStart + removeCount);
                nextSelectionStart = Math.max(lineStart, start - removeCount);
                nextSelectionEnd = nextSelectionStart;
            } else {
                nextValue = value.slice(0, start) + INDENT + value.slice(end);
                nextSelectionStart = start + INDENT.length;
                nextSelectionEnd = nextSelectionStart;
            }
        } else {
            const blockStart = value.lastIndexOf('\n', start - 1) + 1;
            const endLineBreak = value.indexOf('\n', end);
            const blockEnd = endLineBreak === -1 ? value.length : endLineBreak;
            const blockLines = value.slice(blockStart, blockEnd).split('\n');

            if (e.shiftKey) {
                let removedBeforeStart = 0;
                let removedTotal = 0;
                const unindentedLines = blockLines.map((line, index) => {
                    const removed = removeLeadingSpaces(line);
                    if (index === 0) {
                        removedBeforeStart = removed;
                    }
                    removedTotal += removed;
                    return line.slice(removed);
                });
                nextValue = value.slice(0, blockStart) + unindentedLines.join('\n') + value.slice(blockEnd);
                nextSelectionStart = Math.max(blockStart, start - removedBeforeStart);
                nextSelectionEnd = Math.max(nextSelectionStart, end - removedTotal);
            } else {
                const indentedLines = blockLines.map((line) => INDENT + line);
                nextValue = value.slice(0, blockStart) + indentedLines.join('\n') + value.slice(blockEnd);
                nextSelectionStart = start + INDENT.length;
                nextSelectionEnd = end + (INDENT.length * blockLines.length);
            }
        }

        form.setFieldValue(fieldPath, nextValue);
        requestAnimationFrame(() => {
            textarea.setSelectionRange(nextSelectionStart, nextSelectionEnd);
        });
    };

    return (
        <Form
            form={form}
            layout="horizontal"
            onFinish={handleFinish}
            className="location-form"
            labelCol={{style: {width: "100px"}}}
        >
            <Form.Item
                name="path"
                label="路径名称"
                rules={[{required: true, message: '请填写路径名称'}]}
            >
                <Input placeholder="例如：/api/v1"/>
            </Form.Item>

            <Form.Item name="description" label="路径描述">
                <TextArea placeholder="不超过500字"/>
            </Form.Item>

            <Form.Item
                name="proxyPassType"
                label="代理类型"
                rules={[{required: true, message: '请选择代理类型'}]}
            >
                <Radio.Group>
                    {ProxyPassTypeExt.values().map(key => (
                        <Radio key={key} value={ProxyPassType[key]}>
                            {ProxyPassTypeExt.getDescription(key)}
                        </Radio>
                    ))}
                </Radio.Group>
            </Form.Item>

            {proxyPassTypeValue === ProxyPassType[ProxyPassType.APP_KEY] && (
                <Form.Item
                    name="proxyPass"
                    label="服务AppKey"
                    rules={[{required: true, message: '请选择服务AppKey'}]}
                >
                    <Select placeholder="必须先注册到了Nginx服务才会显示在这里"
                            showSearch
                            onDropdownVisibleChange={(open) => {
                                if (open) {
                                    NginxService.listAppKeys().then((appKeys) => {
                                        if (appKeys) {
                                            setAppKeys(appKeys);
                                        }
                                    });
                                }
                            }}
                    >
                        {appKeys.map((appKey) => (
                            <Option key={appKey.appKey} value={appKey.appKey}>
                                {appKey.appKey}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
            )}

            {proxyPassTypeValue === ProxyPassType[ProxyPassType.FRONTEND] && (
                <>
                    <Form.Item
                        name="proxyPass"
                        label="前端appKey"
                        rules={[{required: true, message: '请填写前端appKey'}]}
                    >
                        <Input placeholder="用于拼接代理地址"/>
                    </Form.Item>
                    <Form.Item
                        name="proxyHeaderHost"
                        label="CDN地址"
                        rules={[{required: true, message: '请填写CDN地址'}]}
                    >
                        <Input placeholder="如 cdn.haiskynology.com"/>
                    </Form.Item>
                    <Form.Item
                        name="staticFile"
                        label="静态文件"
                        rules={[{required: false}]}
                    >
                        <Input placeholder="如 manifest.json"/>
                    </Form.Item>
                    <Form.Item
                        name="staticPath"
                        label="静态文件路径"
                        rules={[{required: false}]}
                    >
                        <Input placeholder="如 /images"/>
                    </Form.Item>
                </>
            )}

            {proxyPassTypeValue === ProxyPassType[ProxyPassType.REWRITE] && (
                <Form.Item
                    name="proxyPass"
                    label="重定向地址"
                    rules={[{required: true, message: '请填写重定向地址'}]}
                >
                    <Input placeholder="例如 ^(.*)$ https://$host$1 permanent"/>
                </Form.Item>
            )}

            {proxyPassTypeValue === ProxyPassType[ProxyPassType.RETURN] && (
                <Form.Item
                    name="proxyPass"
                    label="返回值"
                    rules={[{required: true, message: '请填写返回值'}]}
                >
                    <Input placeholder="例如 301 https://www.haiskynology.com$request_uri"/>
                </Form.Item>
            )}

            <Form.Item label="指令配置">
                <Form.List name="configurations">
                    {(fields, {add, remove}) => (
                        <>
                            {
                                availableConfigTypes.length > 0 && (<Dropdown
                                    menu={{
                                        items: availableConfigTypes.map((key) => ({
                                            key: String(key),
                                            label: NginxLocationConfigurationTypeExt.getDescription(key),
                                        })),
                                        onClick: (e) => {
                                            const configType = NginxLocationConfigurationType[Number(e.key)];
                                            add({type: configType, value: ''});
                                        },
                                    }}
                                    trigger={['click']}
                                    open={dropdownVisible}
                                    onOpenChange={setDropdownVisible}>
                                    <Button className="add-location-button"
                                            onClick={() => setDropdownVisible(!dropdownVisible)}>
                                        + 添加指令
                                    </Button>
                                </Dropdown>)
                            }
                            {fields.map(({key, name}) => {
                                const type = NginxLocationConfigurationTypeExt.fromString(form.getFieldValue(['configurations', name, 'type']));
                                return (
                                    <div key={key} className="custom-config-item">
                                        <div className="custom-config-component">
                                            <Form.Item label={NginxLocationConfigurationTypeExt.getDescription(type)}
                                                       name={[name, "value"]}>
                                                {
                                                     type === NginxLocationConfigurationType.ENABLE_WEB_SOCKET && <Switch defaultChecked={true}/>
                                                }
                                                {
                                                    type === NginxLocationConfigurationType.CUSTOM && (
                                                        <TextArea
                                                            className="custom-config-textarea"
                                                            placeholder="填写配置内容"
                                                            autoSize={{ minRows: 1, maxRows: 10 }}
                                                            onKeyDown={(e) => handleCustomConfigTabKeyDown(e, name)}
                                                        />
                                                    )
                                                }
                                                {
                                                    type === NginxLocationConfigurationType.PROXY_READ_TIMEOUT && <ProxyReadTimeoutInput />
                                                }
                                                {
                                                    type === NginxLocationConfigurationType.CLIENT_MAX_BODY_SIZE && <ClientMaxBodySizeInput />
                                                }
                                                {
                                                    type === NginxLocationConfigurationType.DEFAULT_TYPE && (
                                                        <Select
                                                            className="wide-select">
                                                            <Option value="text/plain">text/plain</Option>
                                                            <Option value="text/html">text/html</Option>
                                                            <Option value="text/css">text/css</Option>
                                                            <Option value="application/json">application/json</Option>
                                                            <Option value="application/javascript">application/javascript</Option>
                                                            <Option
                                                                value="application/octet-stream">application/octet-stream</Option>
                                                            <Option value="application/pdf">application/pdf</Option>
                                                            <Option value="application/zip">application/zip</Option>
                                                            <Option value="image/png">image/png</Option>
                                                            <Option value="image/jpeg">image/jpeg</Option>
                                                            <Option value="image/gif">image/gif</Option>
                                                            <Option value="image/tiff">image/tiff</Option>
                                                            <Option value="image/svg+xml ">image/svg+xml</Option>
                                                            <Option value="image/webp">image/webp</Option>
                                                            <Option value="image/x-icon">image/x-icon</Option>
                                                            <Option value="image/x-ms-bmp">image/x-ms-bmp</Option>
                                                            <Option value="audio/midi">audio/midi</Option>
                                                            <Option value="audio/mpeg">audio/mpeg</Option>
                                                            <Option value="audio/ogg">audio/ogg</Option>
                                                            <Option value="audio/x-m4a">audio/x-m4a</Option>
                                                            <Option value="video/mp4">video/mp4</Option>
                                                            <Option value="video/mpeg">video/mpeg</Option>
                                                            <Option value="video/quicktime">video/quicktime</Option>
                                                            <Option value="video/x-flv">video/x-flv</Option>
                                                            <Option value="video/x-m4v">video/x-m4v</Option>
                                                            <Option value="video/x-ms-wmv">video/x-ms-wmv</Option>
                                                            <Option value="video/x-msvideo">video/x-msvideo</Option>
                                                        </Select>
                                                    )
                                                }
                                            </Form.Item>
                                        </div>
                                        <Button danger onClick={() => remove(name)}>删除</Button>
                                    </div>
                                );
                            })}
                        </>
                    )}
                </Form.List>
            </Form.Item>
        </Form>
    );
};

export default LocationForm;